import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Tag, Loader2, Check } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TagChip } from '@/components/contacts/ContactCard';

const COLORS = ['#22C5AB', '#1B2A6B', '#f59e0b', '#ef4444', '#8b5cf6', '#0ea5e9', '#10b981', '#ec4899', '#64748b'];

export const TagPicker = ({ companyId, tags, selected, onChange, onTagsChange, lang }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [busy, setBusy] = useState(false);
  const selectedTags = tags.filter((t) => selected.includes(t.id));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/contact-tags`, { name: name.trim(), color });
      onTagsChange([...tags, data]);
      onChange([...selected, data.id]);
      setName('');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="tag-picker">
      {selectedTags.map((t) => <TagChip key={t.id} tag={t} onRemove={() => toggle(t.id)} />)}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-7 rounded-full text-xs" data-testid="tag-picker-trigger"><Tag size={12} className="mr-1" />{lang === 'id' ? 'Tag' : 'Tags'}</Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {tags.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1">{lang === 'id' ? 'Belum ada tag' : 'No tags yet'}</p>}
            {tags.map((t) => (
              <button type="button" key={t.id} onClick={() => toggle(t.id)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted" data-testid={`tag-option-${t.id}`}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />
                <span className="flex-1 text-left truncate">{t.name}</span>
                {selected.includes(t.id) && <Check size={14} className="text-primary" />}
              </button>
            ))}
          </div>
          <div className="mt-2 border-t border-border pt-2 space-y-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), create())} placeholder={lang === 'id' ? 'Tag baru...' : 'New tag...'} className="h-8 rounded-lg text-sm" data-testid="tag-new-input" />
            <div className="flex items-center gap-1">
              {COLORS.map((c) => <button type="button" key={c} onClick={() => setColor(c)} className={`h-5 w-5 rounded-full border-2 ${color === c ? 'border-foreground' : 'border-transparent'}`} style={{ background: c }} aria-label={c} />)}
              <Button type="button" size="sm" className="ml-auto h-7 rounded-lg" onClick={create} disabled={busy || !name.trim()} data-testid="tag-new-create">{busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
